import { generateClient } from 'aws-amplify/api';
import {
  getCRMAuthInformation,
  getCRMAuthUrl,
  refreshTokenCRM,
} from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { BroadcastChannel } from 'broadcast-channel'

import { isIOS, isTablet } from 'react-device-detect'
import { CRMAccount } from 'src/classes/CRM/CRMIndexedDBTypes';
import { Singleton as IndexDB } from 'src/classes/CRM/CRMIndexedDB';
import { CRMIntegration, CrmIntegrationType } from '@alucio/aws-beacon-amplify/src/models';
import type { VeevaVaultLoginOutput } from '@alucio/aws-beacon-amplify/src/API';
import PWALoaded from 'src/state/global/PWALoaded'
import { LOCAL_STORAGE_CRM_KEYS } from 'src/state/machines/CRM/crmMachineTypes';
import get from 'lodash/get'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy';
import { EXTERNAL_TAB_EVENTS } from './crmMachineTypes';
import * as logger from 'src/utils/logger'
import {
  SYNC_ENTRY_FETCH_TYPE,
  SYNC_ENTRY_REQUEST_TYPE,
  SYNC_ENTRY_STATUS,
  SyncEntry,
} from 'src/classes/CRM/ISyncManifest';
import type { StandaloneForm } from 'src/state/redux/selector/crm';
import type { MEETING_SUBMIT_TYPE } from 'src/state/context/Meetings/MeetingSubmissionTypes';

// Used to dispatch an event and let appsettings know that the CRM integration has been updated
export const CRM_STORAGE_UPDATE = 'CRM_STORAGE_UPDATE'
// Used as a key for the CRM integration session in local storage
export const CRM_AUTH_INFORMATION = 'CRM_AUTH_INFORMATION'
// used to save the code that crm give us to exchange for an access token and refresh token
export const CRM_CONNECTION_CLOSED = 'CRM_CONNECTION_CLOSED'

export const persistVeevaVaultSession = (session: VeevaVaultLoginOutput) => {
  localStorage.setItem(CRM_AUTH_INFORMATION, JSON.stringify(session));
  window.dispatchEvent(new Event(CRM_STORAGE_UPDATE));
}

export const CRM_LAST_SYNC_AT = 'crmLastSyncedAt'

export const CRM_LAST_SYNC_STATUS = 'crmLastSyncStatus'

export const CRM_CODE = 'CRM_CODE'

export const CRM_REDIRECT_INSTANCE = 'CRM_REDIRECT_INSTANCE'

export const INVALID_SESSION_ID = 'INVALID_SESSION_ID'

export const CRM_POST_AUTH_INTENT = 'CRM_POST_AUTH_INTENT'

export const errors = {
  not_allowed_pop_up: 'Make sure you have pop-ups enabled for this site and try again.',
}

const CRM_MACHINE_CHANNEL = 'CRM_MACHINE_CHANNEL'
const crmBroadCastMessage = new BroadcastChannel(CRM_MACHINE_CHANNEL)
const CRM_AUTH_PROFILE_REDIRECT_PATH = '/meetings'

const shouldUseSameTabAuth = () => !!PWALoaded.isPWA || (isIOS && isTablet)

/* Types */
type AccountQuery = {
  Id: string,
  Name: string,
  LastModifiedDate: string,
}

type SalesforceQueryResponse = {
  records: AccountQuery[]
  nextRecordsUrl: string
}

type CRMAuthExchangeError = {
  name?: string
  message?: string
  status?: number
  statusCode?: number
  response?: {
    status?: number
    statusCode?: number
  }
  errors?: Array<{
    message?: string
    status?: number
    statusCode?: number
  }>
}

type RecoverStrandedCRMAuthCodeOptions = {
  isOnline: boolean
  attempts?: number
  delayMs?: number
}

export type CRMPostAuthIntent = {
  type: 'MEETING_SUBMIT',
  meetingId: string,
  submitType?: MEETING_SUBMIT_TYPE,
} | {
  type: 'CRM_FORM_SUBMIT',
  recordId: string,
  standaloneForm: StandaloneForm,
}

export interface CRMIntegrationSession {
  accessToken: string
  refreshToken: string
  encryptedSessionId: string
  authInformation: {
    accessToken: string
    refreshToken: string
    instanceUrl: string
    userInfo: CRMUserInfo
  }
}

export interface CRMUserInfo {
  id: string
  email?: string
  username?: string
  organizationId?: string
  displayName: string
  thumbnail?: string
}

export class CRMUtil {
  // used as a flag to determine if is a crm login or a sso login
readonly CRM_LOGIN_TRIGGERED = 'CRM_LOGIN_TRIGGERED'
// https://github.com/jsforce/jsforce-ajax-proxy

readonly API_VERSION = 'v56.0';

private sfConnectionInstance = () => {
  const authInformation = CRMUtil.getAuthInformation()
  return this.getConnection(authInformation.instanceUrl, authInformation.accessToken)
}

static getAuthInformation = () => {
  const authInformation = localStorage.getItem(LOCAL_STORAGE_CRM_KEYS.CRM_AUTH_INFORMATION)
    ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_CRM_KEYS.CRM_AUTH_INFORMATION) as string)
    : undefined;

  if (!authInformation) {
    logger.salesforceSyncer.debug('No CRM auth information found');
    return undefined;
  }

  return {
    ...authInformation,
    accessToken: authInformation.accessToken,
  };
}

private retryWithDelay = async <T>(fn: Function, attempts: number) => {
  let result;

  while (attempts > 0) {
    result = fn()
    if (result) {
      return result as T;
    }
    attempts--;
    await new Promise(resolve => setTimeout(resolve, 1000));
  }

  return undefined;
}

private isError(e: any): e is { errors: {message: string, path: string[]}[] } {
  return Object.keys(e).includes('errors') &&
  e.errors instanceof Array &&
  e.errors.length > 0
}

private getCRMAuthExchangeErrorDetails = (error: unknown) => {
  const exchangeError = error as CRMAuthExchangeError
  const firstGraphQLError = exchangeError.errors?.[0]

  return {
    statusCode: exchangeError.response?.status ??
      exchangeError.response?.statusCode ??
      exchangeError.status ??
      exchangeError.statusCode ??
      firstGraphQLError?.status ??
      firstGraphQLError?.statusCode,
    errorName: error instanceof Error ? error.name : exchangeError.name,
    errorMessage: error instanceof Error ? error.message : firstGraphQLError?.message ?? String(error),
  }
}

private getCRMRedirectPathAfterAuth = (lastPath?: string, callbackPath?: string) => {
  const redirectPath = lastPath || callbackPath || window.location.pathname

  if (shouldUseSameTabAuth() && (
    redirectPath.startsWith('/profile') ||
    callbackPath?.startsWith('/profile') ||
    window.location.pathname.startsWith('/profile')
  )) {
    return CRM_AUTH_PROFILE_REDIRECT_PATH
  }

  return redirectPath
}

/*
  * @param popUp - boolean to indicate if the login should be done in a pop up window
  */
getCRMAuthUrl = async (popUp: boolean, returnPath?: string) => {
  const appsyncClient = generateClient();
  const { data } = await appsyncClient.graphql({
    query: getCRMAuthUrl,
  });
  let popUpInstance: Window | null = null;
  const url = data?.getCRMAuthUrl;
  // we need to set a temp flag to indicate that we are in the middle of the CRM integration login process
  // this is used to prevent to trigger the sso login process
  // TODO BEAC-3746: try to handle this without using local storage
  localStorage.setItem(this.CRM_LOGIN_TRIGGERED, JSON.stringify({
    triggered: true,
    lastPath: returnPath || `${window.location.pathname}${window.location.search}${window.location.hash}`,
  }));

  if (shouldUseSameTabAuth() && popUp && url && window?.location?.href) {
    // iPad/PWA contexts can block or bypass popups, so use the current tab for CRM OAuth.
    window.location.href = url
    return
  }

  popUp
    ? url && (popUpInstance = window.open(url, '_blank', 'width=600,height=600'))
    : url && window.location.replace(url);

  if (popUp && (!popUpInstance || popUpInstance === null || popUpInstance === undefined)) {
    throw new Error(errors.not_allowed_pop_up);
  }

  if (popUpInstance) {
    const interval = setInterval(() => {
      if (popUpInstance?.closed) {
        this.triggerAuthPopUpClosed()
        clearInterval(interval);
      }
    }, 1000);
  }
}

/**
 * when the user trigger the login in a popup window, we need to wait until the popup is closed
 * and keep validating if the auth token is presented on the local storage
 * if the token is not presented and we trigger the auth logic we will generate an stale workflow
 * so here we are waiting n times to validate if the token is presented after that if the token is not presented
 * we will trigger the CRM_CONNECTION_CLOSED event to let the app know that the CRM integration login was closed
 */
triggerAuthPopUpClosed = async (retryIntervals = 3) => {
  for (let i = 0; i <= retryIntervals + 1; i++) {
    await this.delay(500)

    if (i > retryIntervals) {
      localStorage.removeItem(this.CRM_LOGIN_TRIGGERED);
      CRMUtil.clearPostAuthIntent();
      window.dispatchEvent( new CustomEvent(CRM_CONNECTION_CLOSED));
      break;
    }

    // there are some race conditions that the token is not presented on the local storage we need to wait until the token is presented
    const autInformation = await this.retryWithDelay<CRMIntegrationSession | undefined>(this.loadAuthInformation, 3)
    if (autInformation && autInformation.accessToken) {
      localStorage.removeItem(this.CRM_LOGIN_TRIGGERED);
      window.dispatchEvent( new Event(CRM_STORAGE_UPDATE));
      break;
    }
  }
}

static clearAuthInformation = () => {
  localStorage.removeItem(CRM_AUTH_INFORMATION);
  localStorage.removeItem(CRM_LAST_SYNC_AT);
  CRMUtil.clearPostAuthIntent();
  window.dispatchEvent(new CustomEvent(CRM_CONNECTION_CLOSED));
}

static clearAuthInformationPreservingCachedData = () => {
  localStorage.removeItem(CRM_AUTH_INFORMATION);
  localStorage.removeItem(CRM_CODE);
  localStorage.removeItem(CRM_LAST_SYNC_STATUS);
  window.dispatchEvent(new CustomEvent(CRM_STORAGE_UPDATE));
  window.dispatchEvent(new CustomEvent(CRM_CONNECTION_CLOSED));
}

static setPostAuthIntent = (intent: CRMPostAuthIntent) => {
  sessionStorage.setItem(CRM_POST_AUTH_INTENT, JSON.stringify(intent));
}

static clearPostAuthIntent = () => {
  sessionStorage.removeItem(CRM_POST_AUTH_INTENT);
}

static consumePostAuthIntent = (): CRMPostAuthIntent | undefined => {
  const rawIntent = sessionStorage.getItem(CRM_POST_AUTH_INTENT);
  if (!rawIntent) {
    return undefined;
  }

  CRMUtil.clearPostAuthIntent();

  try {
    return JSON.parse(rawIntent) as CRMPostAuthIntent;
  } catch (error) {
    logger.salesforceSyncer.debug('Unable to parse CRM post-auth intent', error);
    return undefined;
  }
}

/**
    * Get the CRM auth information from the local storage
    * Used when the user is already logged in on the CRM
    * @returns CRM auth information
   */
loadAuthInformation = () : CRMIntegrationSession | undefined => {
  const CRMSettings = CRMUtil.getAuthInformation();

  if (!CRMSettings) {
    logger.salesforceSyncer.debug('No CRM auth information found');
    return undefined;
  }

  return {
    accessToken: CRMSettings?.accessToken || '',
    refreshToken: CRMSettings?.refreshToken || '',
    encryptedSessionId: CRMSettings?.encryptedSessionId || '',
    authInformation: {
      accessToken: CRMSettings?.accessToken || '',
      refreshToken: CRMSettings?.refreshToken || '',
      instanceUrl: CRMSettings?.instanceUrl || '',
      userInfo: {
        id: CRMSettings?.userInfo?.id || '',
        organizationId: CRMSettings?.userInfo?.organizationId || '',
        thumbnail: CRMSettings?.userInfo?.thumbnail || '',
        displayName: CRMSettings?.userInfo?.displayName || '',
      },
    },
  }
}

/*
    Exchange the code returned by the CRM for the access token and refresh token
    @param code - code returned by the CRM login workflow
    @returns CRM auth information Access token and refresh token
  */
getCRMAuthInformation = async (code: string, dispatchEvent: boolean = true) => {
  const appsyncClient = generateClient();
  let data

  try {
    const response = await appsyncClient.graphql({
      query: getCRMAuthInformation,
      variables: {
        code,
      },
    });
    data = response.data
  } catch (error) {
    logger.salesforceSyncer.error('Unable to get CRM auth information', this.getCRMAuthExchangeErrorDetails(error))
    throw error
  }

  const authInformation = data?.getCRMAuthInformation
  if (!authInformation) {
    logger.salesforceSyncer.error('No data returned from getCrmAuthInformation');
    return undefined;
  }

  localStorage.setItem(CRM_AUTH_INFORMATION, JSON.stringify(authInformation));
  localStorage.removeItem(this.CRM_LOGIN_TRIGGERED)
  // By default storage event is if is triggered from other tab, but also we need to trigger it from the current tab
  if (dispatchEvent) {
    window.dispatchEvent( new Event(CRM_STORAGE_UPDATE) )
  }
  return authInformation;
}

recoverStrandedCRMAuthCode = async ({
  isOnline,
  attempts = 3,
  delayMs = 1000,
}: RecoverStrandedCRMAuthCodeOptions): Promise<boolean> => {
  if (!isIOS || !isOnline || localStorage.getItem(CRM_AUTH_INFORMATION)) {
    return false
  }

  const crmCode = localStorage.getItem(CRM_CODE)
  if (!crmCode) {
    return false
  }

  // Repairs iPad sessions where the CRM redirect code was saved but never exchanged.
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const authInformation = await this.getCRMAuthInformation(crmCode)
      if (authInformation) {
        localStorage.removeItem(CRM_CODE)
        localStorage.removeItem(CRM_LAST_SYNC_STATUS)
        logger.salesforceSyncer.debug('Recovered stranded CRM code and auth information updated')
        // Reload so app state machines restart with CRM auth information already available.
        window.location.reload()
        return true
      }
    } catch (error) {
      logger.salesforceSyncer.debug('Unable to recover stranded CRM auth code', {
        attempt,
        attempts,
        errorName: error instanceof Error ? error.name : undefined,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
    }

    if (attempt < attempts && delayMs > 0) {
      await this.delay(delayMs)
    }
  }

  localStorage.removeItem(CRM_CODE)
  localStorage.removeItem(CRM_LAST_SYNC_STATUS)
  localStorage.removeItem(this.CRM_LOGIN_TRIGGERED)
  sessionStorage.removeItem(CRM_REDIRECT_INSTANCE)
  logger.salesforceSyncer.debug('Cleared stranded CRM auth code after recovery attempts failed', {
    attempts,
  })

  return false
}

/**
   * We need a way to trigger the CRM login workflow
   * so when the crm return the url with the code on the url
   * https://example.com/login?code=XFGFHJHJ
   * we can exchange the code for the access token and refresh token calling the getCRMAuthInformation GraphQL mutation
   * this method remove the code from the url and navigate to the latest url used by the user normally would be <profile />
   * we need to remove the code from the url to avoid having collisions with the cognito/amplify sso
   */
interceptCRMAuthCode = async () => {
  const queryString = window.location.search;
  const callbackPath = window.location.pathname;
  const urlParams = new URLSearchParams(queryString);
  const code = urlParams.get('code')
  const isCRMRedirectInstance = sessionStorage.getItem(CRM_REDIRECT_INSTANCE) === 'true';
  const openedBySameDomain = window.opener?.location.origin === window.location.origin;
  const crmCode = localStorage.getItem(CRM_CODE)
  const CRMLogin = localStorage.getItem(this.CRM_LOGIN_TRIGGERED)
    ? JSON.parse(localStorage.getItem(this.CRM_LOGIN_TRIGGERED) as string)
    : undefined;

  logger.salesforceSyncer.debug('[CRM_SYNC_DEBUG] intercept CRM auth code', {
    callbackPath,
    hasCode: Boolean(code),
    hasStoredCRMCode: Boolean(crmCode),
    isCRMRedirectInstance,
    loginTriggered: Boolean(CRMLogin?.triggered),
    openedBySameDomain,
  })

  /*
     If the login is triggered by CRM Integration, then we need to intercept the redirect and get the code
     issued by CRM Integration and reload the page without the code to avoid issues with the SSO flow
    */
  if (code && CRMLogin?.triggered) {
    localStorage.removeItem(this.CRM_LOGIN_TRIGGERED)
    logger.salesforceSyncer.debug('[CRM_SYNC_DEBUG] processing CRM redirect code', {
      callbackPath,
      mode: openedBySameDomain ? 'popup' : 'same-tab',
    })
    /*
      Popup redirects should hand the code back to the opener and close.
     */
    if (openedBySameDomain) {
      localStorage.setItem(CRM_CODE, code)
      sessionStorage.setItem(CRM_REDIRECT_INSTANCE, 'true');
      window.close()
    } else {
      // Same-tab redirects have no opener, so this tab must exchange the code itself.
      await this.getCRMAuthInformation(code, false)
      localStorage.removeItem(CRM_CODE)
      localStorage.removeItem('isFromTabletProfile')
      localStorage.removeItem('hasRedirectedFromProfile')
      const redirectPath = this.getCRMRedirectPathAfterAuth(CRMLogin.lastPath, callbackPath)
      logger.salesforceSyncer.debug('[CRM_SYNC_DEBUG] CRM auth information exchanged in same tab', {
        callbackPath,
        redirectPath,
      })
      window.history.replaceState({}, '', redirectPath)
      if (shouldUseSameTabAuth()) {
        window.dispatchEvent(typeof PopStateEvent === 'function'
          ? new PopStateEvent('popstate', { state: window.history.state })
          : new Event('popstate'))
      }
      window.dispatchEvent( new Event(CRM_STORAGE_UPDATE) )
    }
  }
  /*
      after the page is reloaded with the code, we can get the CRM Auth Information
      then we delete the code from the local storage
    */
  else if (crmCode && !isCRMRedirectInstance) {
    logger.salesforceSyncer.debug('[CRM_SYNC_DEBUG] processing stored CRM code')
    const authInformation = await this.getCRMAuthInformation(crmCode)
    localStorage.setItem(CRM_AUTH_INFORMATION, JSON.stringify(authInformation))
    localStorage.removeItem(CRM_CODE)
  }
}

/*
    Refresh the access token and refresh token
  */
refreshTokenCRM = async (accessToken: string, refreshToken: string, dispatchEvent: boolean = true) => {
  const appsyncClient = generateClient();
  const { data } = await appsyncClient.graphql({
    query: refreshTokenCRM,
    variables: {
      accessToken,
      refreshToken,
    },
  });
  if (!data) {
    console.error('No data returned from refreshTokenCRM');
    return undefined;
  }

  const authInformation = CRMUtil.getAuthInformation();

  if (!authInformation) {
    console.error('No CRM auth information found');
    return undefined;
  }

  localStorage.setItem(CRM_AUTH_INFORMATION, JSON.stringify({
    ...authInformation,
    accessToken: data.refreshTokenCRM?.accessToken,
    issuedAt: data.refreshTokenCRM?.issuedAt,
  }))

  // only trigger event on the state machine, when is invoked as a helper function avoid to trigger the event to avoid the state machine to trigger the sync process
  if (dispatchEvent) {
  // By default storage event is if is triggered from other tab, but also we need to trigger it from the current tab
    window.dispatchEvent( new Event(CRM_STORAGE_UPDATE) )
  }
  return data.refreshTokenCRM;
}

/*
   Clean all the variables related to the CRM auth that may/may not be saved on the local storage
   */
cleanLogOutCRMStorage = () => {
  localStorage.removeItem(CRM_AUTH_INFORMATION);
  localStorage.removeItem(this.CRM_LOGIN_TRIGGERED);
  localStorage.removeItem(CRM_CODE);
  localStorage.removeItem(CRM_STORAGE_UPDATE);
  localStorage.removeItem(CRM_LAST_SYNC_AT);
  localStorage.removeItem(CRM_LAST_SYNC_STATUS);
  CRMUtil.clearPostAuthIntent();
  IndexDB.clearDB();
  window.dispatchEvent( new Event(CRM_STORAGE_UPDATE) )
}

private handleSalesforceFetch = async (url: string, accessToken: string): Promise<SalesforceQueryResponse> => {
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
  return response.json();
};

// SALES FORCE API
getConnection = (instanceUrl: string, accessToken: string) => {
  return {
    instanceUrl,
    accessToken,
    query: (soql: string): Promise<SalesforceQueryResponse> => {
      const url = `${instanceUrl}/services/data/${this.API_VERSION}/query?q=${encodeURIComponent(soql)}`;
      return this.handleSalesforceFetch(url, accessToken);
    },
    describe: (sObjectName: string): Promise<SalesforceQueryResponse> => {
      const url = `${instanceUrl}/services/data/${this.API_VERSION}/sobjects/${sObjectName}/describe`;
      return this.handleSalesforceFetch(url, accessToken);
    },
    request: (path: string): Promise<SalesforceQueryResponse> => {
      const url = `${instanceUrl}${path}`;
      return this.handleSalesforceFetch(url, accessToken);
    },
  };
}

private delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

private template = (tpl, args) => tpl.replace(/\${(\w+)}/g, (_, v) => args[v]);

getAccountData = async (config: CRMIntegration, lastSyncTime?: number) : Promise<{
  error: { errorCode: string, message: string } | undefined,
  records: CRMAccount[],
  lastSyncTime: number,
} | undefined
> => {
  const conn = this.sfConnectionInstance();
  if (!conn) {
    return undefined;
  }
  const lastSyncDate = lastSyncTime ? new Date(lastSyncTime).toISOString() : new Date(0).toISOString()

  let records : CRMAccount[] = []
  let error: { errorCode: string, message: string } | undefined;
  let nextRecordsUrl = '';
  while (true) {
    const query = this.template(config.accountsSettings.query, { lastSyncDate: lastSyncDate });
    const accounts = nextRecordsUrl
      ? await conn.request(nextRecordsUrl)
      : await conn.query(query);

    if (Array.isArray(accounts) && accounts[0]?.errorCode) {
      error = accounts[0]
      break;
    } else if (!accounts.records || !accounts?.records?.length) {
      break;
    }

    const items = accounts.records.map<CRMAccount>((account) => ({
      ...omit(account, ['Id']),
      id: account.Id,
      addresses: get(account, `${config.accountsSettings.addressSettings.alias}.records`, [])
        /** TODO: This typing should be unnecessary, see:
         * https://alucioinc.atlassian.net/browse/BEAC-4340
         */
        .map((address: {Id: string}) => ({
          id: address.Id,
          ...omit(address, ['Id']),
        })),
    }))

    records = [...records, ...items]

    if (accounts.nextRecordsUrl) {
      nextRecordsUrl = accounts.nextRecordsUrl;
    } else {
      break;
    }
  }

  return {
    // a bad query can return duplicated records
    // this will lead on multiple exceptions on the indexedDB
    error,
    records: uniqBy(records, 'id'),
    lastSyncTime: new Date().getTime(),
  }
}

externalTabHandler = (
  _callback: (event : EXTERNAL_TAB_EVENTS) => void,
  _receive: Function,
): void => {
  crmBroadCastMessage.onmessage = (event: any) => {
    const type = event?.data?.type ?? event?.type
    if (type === 'LOGOUT_CRM_FROM_OTHER_TAB') {
      _callback({ type: 'LOGOUT_CRM_FROM_OTHER_TAB' })
    }
    else if (type === 'LOGIN_CRM_FROM_OTHER_TAB') {
      // // TODO: find a way to do this in the state machine without syncing
      // reloadApp()
      _callback({ type: 'LOGIN_CRM_FROM_OTHER_TAB' })
    }
  }

  _receive((event: { type: string; }) => {
    if (event.type === 'LOGOUT_CRM_FROM_OTHER_TAB') {
      crmBroadCastMessage.postMessage({ type: 'LOGOUT_CRM_FROM_OTHER_TAB' })
    }
    else if (event.type === 'LOGIN_CRM_FROM_OTHER_TAB') {
      crmBroadCastMessage.postMessage({ type: 'LOGIN_CRM_FROM_OTHER_TAB' })
    }
  })
}

setOfflineCRM(): any {
  this.cleanLogOutCRMStorage()
}
}

export function getTablesSyncEntries(config: CRMIntegration, baseUrl: string): SyncEntry[] {
  const meetingSettings = config.meetingSetting;
  const entries: SyncEntry[] = [];
  const crmForms: { apiName: string, parentTable?: string, isStandaloneForm?: boolean }[] = [];

  if (!meetingSettings && !config.crmStandaloneForms?.length) return entries

  if (meetingSettings) {
    crmForms.push({
      apiName: meetingSettings.apiName,
    });
    config.meetingSetting?.children.forEach((childForm) => {
      crmForms.push({
        apiName: childForm.apiName,
        parentTable: meetingSettings.apiName,
      });
    });
  }

  config.crmStandaloneForms?.forEach((form) => {
    crmForms.push({
      apiName: form.apiName,
      isStandaloneForm: true,
      parentTable: form.parents?.[0]?.apiName,
    });
    form.children.forEach((childForm) => {
      crmForms.push({
        apiName: childForm.apiName,
        isStandaloneForm: true,
        parentTable: form.apiName,
      });
    });
  });

  const lastSynced = new Date().getTime();
  crmForms.forEach((formSetting) =>
    entries.push({
      url: `${baseUrl}/${formSetting.apiName}`,
      parameters: {
        isStandaloneForm: !!formSetting.isStandaloneForm,
        parentId: formSetting.parentTable,
        apiName: formSetting.apiName,
        id: formSetting.apiName,
      },
      bypassError: formSetting.apiName !== meetingSettings?.apiName,
      type: config.crmIntegrationType as CrmIntegrationType,
      subType: SYNC_ENTRY_REQUEST_TYPE.TABLE,
      fetchType: SYNC_ENTRY_FETCH_TYPE.REGULAR,
      id: formSetting.apiName,
      lastSynced,
      status: SYNC_ENTRY_STATUS.PENDING,
    }));

  return entries;
}
